import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import { logError } from "../utils/logger";

/**
 * Hook for managing like state for a single note
 * Handles optimistic updates and syncs with backend
 *
 * @param {string} noteId - The ID of the note
 * @param {boolean} initialLiked - Initial liked state (from backend data)
 * @param {number} initialCount - Initial like count (from backend data)
 */
export function useNoteLike(noteId, initialLiked = false, initialCount = 0) {
  const [liked, setLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);

  const toggleLike = useCallback(async () => {
    if (loading || !noteId) return;

    // Optimistic update
    const newLiked = !liked;
    const newCount = newLiked ? likeCount + 1 : Math.max(0, likeCount - 1);

    /**
     * Used when updating the backend fails, so we can retain the true values
     */
    const previousLiked = liked;
    const previousCount = likeCount;

    setLiked(newLiked);
    setLikeCount(newCount);
    setLoading(true);

    try {
      const response = await api.toggleLike(noteId);

      // Update with backend response (should match optimistic update)
      setLiked(response.data.liked);
      setLikeCount(response.data.count);
    } catch (err) {
      // Revert on error
      logError("Failed to toggle like:", err);
      setLiked(previousLiked);
      setLikeCount(previousCount);
    } finally {
      setLoading(false);
    }
  }, [noteId, liked, likeCount, loading]);

  return {
    liked,
    likeCount,
    toggleLike,
    loading,
  };
}
